// import React, { useEffect } from "react";

// interface CopyPasteProviderProps {
//   children: React.ReactNode;
//   enableCopyPasteIn?: string; // optional selector to allow copy/paste
// }

// const CopyPasteProvider: React.FC<CopyPasteProviderProps> = ({
//   children,
//   enableCopyPasteIn,
// }) => {
//   useEffect(() => {
//     const disableCopyCutPaste = (e: ClipboardEvent) => {
//       if (enableCopyPasteIn) {
//         // Allow copy/paste inside elements matching selector
//         const target = e.target as HTMLElement;
//         if (target.closest(enableCopyPasteIn)) return;
//       }
//       e.preventDefault();
//     };

//     const disableRightClick = (e: MouseEvent) => {
//       if (enableCopyPasteIn) {
//         const target = e.target as HTMLElement;
//         if (target.closest(enableCopyPasteIn)) return;
//       }
//       e.preventDefault();
//     };

//     document.addEventListener("cut", disableCopyCutPaste);
//     document.addEventListener("copy", disableCopyCutPaste);
//     document.addEventListener("paste", disableCopyCutPaste);
//     // document.addEventListener("contextmenu", disableRightClick);

//     return () => {
//       document.removeEventListener("cut", disableCopyCutPaste);
//       document.removeEventListener("copy", disableCopyCutPaste);
//       document.removeEventListener("paste", disableCopyCutPaste);
//       // document.removeEventListener("contextmenu", disableRightClick);
//     };
//   }, [enableCopyPasteIn]);

//   return <>{children}</>;
// };

// export default CopyPasteProvider;

import React, { useEffect, useState, useRef } from "react";

interface CopyPasteProviderProps {
  children: React.ReactNode;
  enableCopyPasteIn?: string; // optional selector to allow copy/paste
}

const CopyPasteProvider: React.FC<CopyPasteProviderProps> = ({
  children,
  enableCopyPasteIn,
}) => {
  const [menuPosition, setMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [lastFocusedElement, setLastFocusedElement] =
    useState<HTMLElement | null>(null);
  const [hasSelectedText, setHasSelectedText] = useState(false);
  const hiddenInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const disableCopyCutPaste = (e: ClipboardEvent) => {
      if (enableCopyPasteIn) {
        const target = e.target as HTMLElement;
        if (target.closest(enableCopyPasteIn)) return;
      }
      e.preventDefault();
    };

    const handleRightClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Store the currently focused element
      if (document.activeElement instanceof HTMLElement) {
        setLastFocusedElement(document.activeElement);
      }

      // Check if any text is selected
      const selection = window.getSelection();
      const hasTextSelection = selection?.toString().length! > 0;
      setHasSelectedText(!!hasTextSelection);

      // Inside allowed selector → show custom Copy/Paste menu
      if (enableCopyPasteIn && target.closest(enableCopyPasteIn)) {
        e.preventDefault(); // block browser menu
        setMenuPosition({ x: e.pageX, y: e.pageY });
        return;
      }

      // Elsewhere → block right-click entirely
      e.preventDefault();
    };

    const handleClick = () => setMenuPosition(null);

    // Also check selection on mouse up (when text selection completes)
    const handleMouseUp = () => {
      const selection = window.getSelection();
      setHasSelectedText(selection?.toString().length! > 0);
    };

    document.addEventListener("cut", disableCopyCutPaste);
    document.addEventListener("copy", disableCopyCutPaste);
    document.addEventListener("paste", disableCopyCutPaste);
    document.addEventListener("contextmenu", handleRightClick);
    document.addEventListener("click", handleClick);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("cut", disableCopyCutPaste);
      document.removeEventListener("copy", disableCopyCutPaste);
      document.removeEventListener("paste", disableCopyCutPaste);
      document.removeEventListener("contextmenu", handleRightClick);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [enableCopyPasteIn]);

  const handleCopy = async () => {
    if (!hasSelectedText) return;

    try {
      const selectedText = window.getSelection()?.toString();
      if (selectedText) {
        await navigator.clipboard.writeText(selectedText);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
      // Fallback for browsers that don't support clipboard API
      document.execCommand("copy");
    }
    setMenuPosition(null);
  };

  const handlePaste = async () => {
    try {
      // Focus back to the last focused element first
      if (lastFocusedElement) {
        lastFocusedElement.focus();
      }

      // Read from clipboard
      const text = await navigator.clipboard.readText();

      // Insert the text at cursor position
      if (lastFocusedElement) {
        if (
          lastFocusedElement instanceof HTMLInputElement ||
          lastFocusedElement instanceof HTMLTextAreaElement
        ) {
          // For input and textarea elements
          const element = lastFocusedElement;
          const start = element.selectionStart || 0;
          const end = element.selectionEnd || 0;
          const value = element.value;

          element.value =
            value.substring(0, start) + text + value.substring(end);
          element.selectionStart = element.selectionEnd = start + text.length;

          // Trigger change event
          const event = new Event("input", { bubbles: true });
          element.dispatchEvent(event);
        } else if (lastFocusedElement.isContentEditable) {
          // For contenteditable elements
          document.execCommand("insertText", false, text);
        } else {
          // Fallback for other elements
          document.execCommand("insertText", false, text);
        }
      }
    } catch (err) {
      console.error("Failed to paste:", err);
      // Fallback using execCommand
      if (lastFocusedElement) {
        lastFocusedElement.focus();
        try {
          document.execCommand("paste");
        } catch (e) {
          console.error("execCommand paste also failed:", e);
        }
      }
    }
    setMenuPosition(null);
  };

  const handleSelectAll = () => {
    if (lastFocusedElement) {
      lastFocusedElement.focus();

      if (
        lastFocusedElement instanceof HTMLInputElement ||
        lastFocusedElement instanceof HTMLTextAreaElement
      ) {
        lastFocusedElement.select();
        setHasSelectedText(true);
      } else if (lastFocusedElement.isContentEditable) {
        document.execCommand("selectAll", false);
        setHasSelectedText(true);
      } else {
        const range = document.createRange();
        range.selectNodeContents(lastFocusedElement);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        setHasSelectedText(true);
      }
    }
    setMenuPosition(null);
  };

  return (
    <>
      {children}

      {/* Hidden textarea for fallback clipboard operations */}
      <textarea
        ref={hiddenInputRef}
        style={{
          position: "fixed",
          left: "-1000px",
          top: "-1000px",
          opacity: 0,
          width: "1px",
          height: "1px",
        }}
        readOnly
      />

      {menuPosition && (
        <ul
          style={{
            position: "absolute",
            top: `${menuPosition.y}px`,
            left: `${menuPosition.x}px`,
            background: `radial-gradient(
    99.45% 99.45% at 50% 100%,
    #1e3852 0%,
    #18232e 100%
  )`,
            fontFamily: "Mazzard",

            color: "white",
            border: "1px solid #ccc",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0)",
            listStyle: "none",
            margin: 0,
            padding: "5px 0",
            zIndex: 9999,
            width: "150px",
          }}
        >
          <li
            onClick={handleCopy}
            style={{
              padding: "8px 12px",
              cursor: hasSelectedText ? "pointer" : "not-allowed",
              fontSize: "13px",
              fontFamily: "Mazzard",
              color: hasSelectedText ? "white" : "#999",
              userSelect: "none",
              // backgroundColor: hasSelectedText ? "transparent" : "#f9f9f9",
            }}
            // onMouseEnter={(e) => {
            //   if (hasSelectedText) {
            //     (e.target as HTMLElement).style.backgroundColor = "#f0f0f0";
            //   }
            // }}
            // onMouseLeave={(e) =>
            //   ((e.target as HTMLElement).style.backgroundColor = hasSelectedText
            //     ? "transparent"
            //     : "#f9f9f9")
            // }
          >
            Copy
          </li>
          <li
            onClick={handlePaste}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "13px",
              // color: "#333",
              fontFamily: "Mazzard",
              userSelect: "none",
            }}
            // onMouseEnter={(e) =>
            //   ((e.target as HTMLElement).style.backgroundColor = "#f0f0f0")
            // }
            // onMouseLeave={(e) =>
            //   ((e.target as HTMLElement).style.backgroundColor = "transparent")
            // }
          >
            Paste
          </li>
          {/* <li
            onClick={handleSelectAll}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#333",
              userSelect: "none",
              borderTop: "1px solid #f0f0f0",
            }}
            onMouseEnter={(e) =>
              ((e.target as HTMLElement).style.backgroundColor = "#f0f0f0")
            }
            onMouseLeave={(e) =>
              ((e.target as HTMLElement).style.backgroundColor = "transparent")
            }
          >
            Select All
          </li> */}
        </ul>
      )}
    </>
  );
};

export default CopyPasteProvider;
